import type {
  PaymentOption,
  v2CustomerFindingDriverStatus,
} from "@be-miniapp/api"
import { BEAutoRideStatus } from "@be-miniapp/types"
import type {
  DriverAcceptedData,
  FullEngagementInfo,
  TripState,
} from "@be-miniapp/types"

import type { RideEngagement } from "../stores/ride-contracts"
import type {
  HydrateInTripSessionParams,
  RideLocation,
} from "../stores/ride-data"

export function deriveTripStateFromEngagementStatus(params: {
  status: BEAutoRideStatus
  driverInPerfectRide?: boolean
}): TripState {
  const { status, driverInPerfectRide } = params

  let derivedState: TripState = "finding-driver"
  switch (status) {
    case BEAutoRideStatus.arrived: {
      derivedState = "driver-arrived"
      break
    }
    case BEAutoRideStatus.requested: {
      derivedState = "finding-driver"
      break
    }
    case BEAutoRideStatus.accepted: {
      derivedState = "driver-found"
      break
    }
    case BEAutoRideStatus.timedout: {
      derivedState = "no-driver"
      break
    }
    case BEAutoRideStatus.started: {
      derivedState = "ride-started"
      break
    }
    case BEAutoRideStatus.ended: {
      derivedState = "ride-finished"
      break
    }
  }

  return driverInPerfectRide ? "perfect-ride" : derivedState
}

export function buildRideLocationFromEngagementPickup(params: {
  engagement: DriverAcceptedData | FullEngagementInfo
}): RideLocation {
  const engagement = params.engagement
  return {
    type: "pick-up",
    name:
      (engagement?.pickup_location_address_name ?? "").split(",")?.[0] ?? "",
    address: engagement?.pickup_location_address ?? "",
    placeId: "",
    location: {
      latitude: engagement?.pickup_latitude,
      longitude: engagement?.pickup_longitude,
    },
    isConfirmed: true,
  }
}

export function buildRideLocationFromEngagementDropoff(params: {
  engagement: DriverAcceptedData | FullEngagementInfo
}): RideLocation {
  const engagement = params.engagement
  return {
    type: "drop-off",
    name: engagement?.drop_location_address_name,
    address: engagement?.drop_location_address ?? "",
    placeId: "",
    location: {
      latitude: engagement?.op_drop_latitude,
      longitude: engagement?.op_drop_longitude,
    },
    isConfirmed: true,
  }
}

export function buildHydrateParamsFromEngagement(params: {
  sessionId: number
  engagement: RideEngagement | DriverAcceptedData | FullEngagementInfo
  nextState: TripState
  paymentMethod?: PaymentOption
  preferredPaymentMethod?: number
}): HydrateInTripSessionParams {
  const {
    sessionId,
    engagement,
    nextState,
    paymentMethod,
    preferredPaymentMethod,
  } = params

  return {
    sessionId,
    engagement,
    pickup: buildRideLocationFromEngagementPickup({ engagement }),
    dropoff: buildRideLocationFromEngagementDropoff({ engagement }),
    state: nextState,
    paymentMethod,
    preferredPaymentMethod,
  }
}

export function buildHydrateParamsFromFindingDriver(params: {
  sessionId: number
  findingDriverData: v2CustomerFindingDriverStatus
  paymentMethod?: PaymentOption
  preferredPaymentMethod?: number
}): HydrateInTripSessionParams {
  const {
    sessionId,
    findingDriverData,
    paymentMethod,
    preferredPaymentMethod,
  } = params

  return {
    sessionId,
    pickup: {
      type: "pick-up",
      name: findingDriverData.pickup_location_address_name,
      address: findingDriverData.pickup_location_address,
      placeId: "",
      location: {
        latitude: findingDriverData.latitude,
        longitude: findingDriverData.longitude,
      },
      isConfirmed: true,
    },
    dropoff: {
      type: "drop-off",
      name: findingDriverData.drop_location_address_name,
      address: findingDriverData.drop_location_address,
      placeId: "",
      location: {
        latitude: findingDriverData.op_drop_latitude,
        longitude: findingDriverData.op_drop_longitude,
      },
      isConfirmed: true,
    },
    state: "finding-driver",
    paymentMethod,
    preferredPaymentMethod,
  }
}
